import { Controller } from "@hotwired/stimulus";
import { creationOptionsFromJSON, credentialToJSON, requestOptionsFromJSON } from "./shared/webauthn_json";

const supportsWebAuthn = (): boolean =>
  typeof window.PublicKeyCredential !== "undefined" && typeof navigator.credentials !== "undefined";

const disableWebAuthnInputs = (): void => {
  document.querySelectorAll<HTMLInputElement>("[data-requires-webauthn]").forEach(input => {
    input.disabled = true;
    if (!input.checked) return;

    input.checked = false;
    const fallback = Array.from(document.querySelectorAll<HTMLInputElement>("input[type='radio']")).find(
      radio => radio.name === input.name && !radio.disabled
    );
    if (fallback) fallback.checked = true;
  });
  document.querySelectorAll<HTMLElement>("[data-webauthn-unsupported-hint]").forEach(hint => {
    hint.hidden = false;
  });
};

export default class extends Controller<HTMLElement> {
  static values = {
    mode: String,
    options: Object,
    unsupportedMessage: String,
    waitingMessage: String,
    errorMessage: String,
  };
  static targets = ["code", "name", "status", "button", "fallback"];

  declare readonly modeValue: string;
  declare readonly optionsValue: object;
  declare readonly unsupportedMessageValue: string;
  declare readonly waitingMessageValue: string;
  declare readonly errorMessageValue: string;
  declare readonly codeTarget: HTMLInputElement;
  declare readonly hasCodeTarget: boolean;
  declare readonly nameTarget: HTMLInputElement;
  declare readonly hasNameTarget: boolean;
  declare readonly statusTarget: HTMLElement;
  declare readonly hasStatusTarget: boolean;
  declare readonly buttonTarget: HTMLButtonElement;
  declare readonly hasButtonTarget: boolean;
  declare readonly fallbackTarget: HTMLElement;
  declare readonly hasFallbackTarget: boolean;

  connect(): void {
    if (supportsWebAuthn()) {
      this.useHiddenCode();
      if (this.modeValue === "get") this.setSubmitButtonsHidden(true);
      return;
    }

    disableWebAuthnInputs();
    if (this.hasButtonTarget) this.buttonTarget.disabled = true;
    this.showStatus(this.unsupportedMessageValue);
    this.showFallback();
  }

  async start(event?: Event): Promise<void> {
    event?.preventDefault();
    if (!this.hasCodeTarget) return;

    this.buttonTarget.disabled = true;
    this.showStatus(this.waitingMessageValue);

    try {
      const credential = await this.requestCredential();
      if (!credential) throw new Error("No credential returned");

      this.submit(this.payloadFor(credential));
    } catch {
      this.buttonTarget.disabled = false;
      this.showStatus(this.errorMessageValue);
      this.showFallback();
    }
  }

  showFallback(event?: Event): void {
    event?.preventDefault();
    if (!this.hasFallbackTarget) return;

    const manualInput = this.manualInput();
    this.fallbackTarget.hidden = false;
    this.setSubmitButtonsHidden(false);
    if (!manualInput) return;

    manualInput.disabled = false;
    if (this.hasCodeTarget) this.codeTarget.disabled = true;
    manualInput.focus();
  }

  private requestCredential(): Promise<Credential | null> {
    if (this.modeValue === "create") {
      const json = this.optionsValue as PublicKeyCredentialCreationOptionsJSON;
      return navigator.credentials.create({ publicKey: creationOptionsFromJSON(json) });
    }

    const json = this.optionsValue as PublicKeyCredentialRequestOptionsJSON;
    return navigator.credentials.get({ publicKey: requestOptionsFromJSON(json) });
  }

  private payloadFor(credential: Credential): Record<string, unknown> {
    const payload = credentialToJSON(credential as PublicKeyCredential);
    const name = this.hasNameTarget ? this.nameTarget.value.trim() : "";
    if (this.modeValue === "create" && name) payload.name = name;

    return payload;
  }

  private submit(payload: Record<string, unknown>): void {
    this.useHiddenCode();
    this.codeTarget.value = JSON.stringify(payload);
    this.codeTarget.form?.requestSubmit();
  }

  private useHiddenCode(): void {
    const manualInput = this.manualInput();
    if (manualInput) manualInput.disabled = true;
    if (this.hasCodeTarget) this.codeTarget.disabled = false;
  }

  private setSubmitButtonsHidden(hidden: boolean): void {
    this.element
      .closest("form")
      ?.querySelectorAll<HTMLElement>("button[type='submit'], input[type='submit']")
      .forEach(button => {
        button.hidden = hidden;
      });
  }

  private manualInput(): HTMLInputElement | null {
    return this.hasFallbackTarget
      ? this.fallbackTarget.querySelector<HTMLInputElement>("input[name='twofa_code']")
      : null;
  }

  private showStatus(message: string): void {
    if (!this.hasStatusTarget) return;

    this.statusTarget.textContent = message;
    this.statusTarget.hidden = !message;
  }
}
